// @givmo/design-system — ErrorMessage (ports Flutter `ErrorMessage`, error_message.dart).
//
// Inline field-error row: info icon + message text, 8px top padding and a 4px gap. Lucide's
// `Info` backs the icon (recon icon-mapping).
//
// TONE: the default `error` tone renders the semantic `danger-700` token. It replaces the
// Flutter port's literal `magenta` (#D15296), which failed the 4.5:1 text floor everywhere it
// landed — 3.92:1 on white, 3.60:1 inside a `bg-danger-50` box — and read as a hue collision
// against those danger-tinted containers. The component cannot know its own ground, so the token
// has to clear the floor on ALL of them rather than be chosen per-surface: danger-700 is 6.57:1
// on white, 6.05:1 on danger-50 and 6.28:1 on surface-sunken. (danger-600 would pass on white at
// 4.83:1 but fail inside a danger-50 box at 4.44:1, which is why it is not the choice here.)
// The `muted` tone uses the `text-muted` token (#888888) — the recon flagged that the per-app
// `error_reference_text` shells share that off-palette gray; `muted` gives that later port
// (03-3b/c/d) a token home instead of re-introducing the literal.
//
// WRAP: `text-pretty` (`text-wrap: pretty`), so no message ends on a line of one word (Board's
// "…Reload the page to see what's there now." left "now." alone in a 448-wide dialog; cut copy4).
// It sits on the row, which the text inherits it from, so a consumer's own wrap class (the company
// newcomer door's `text-balance`) still wins through `cn`'s merge.
import { Info } from 'lucide-react';
import { cn } from '../lib/utils';
import { testIdProp, type TestIdProps } from '../lib/testid';
import { Icon } from './icon';

export interface ErrorMessageProps extends TestIdProps {
  /** The message text. */
  children: React.ReactNode;
  /** `error` = danger-700 (default); `muted` = text-muted #888888. */
  tone?: 'error' | 'muted';
  className?: string;
}

export function ErrorMessage({ children, tone = 'error', className, testId }: ErrorMessageProps) {
  const isError = tone === 'error';
  return (
    <div
      role={isError ? 'alert' : undefined}
      className={cn('flex items-start gap-4 pt-8 text-pretty', className)}
      {...testIdProp(testId)}
    >
      <Icon
        icon={Info}
        size={16}
        className={cn('mt-[2px]', isError ? 'text-danger-700' : 'text-text-muted')}
        aria-hidden
      />
      <span
        className={cn(
          'font-sans text-s12 font-w600 tracking-givmo',
          isError ? 'text-danger-700' : 'text-text-muted',
        )}
      >
        {children}
      </span>
    </div>
  );
}
